import { ensureFetchDelay } from '../../helpers/fetch.js';
import svpApiClient from '../../services/svpApiClient.js';
import { config } from '../../config.js';

import { getCategoryShortPath } from './categoryHelpers.js';

import type { Vendor } from '../../types.js';
import type {
  SvpCategory,
  SvpCategoryAdditional,
} from '@vgtv/api-client/lib/svp_category';

/**
 * Fetches all categories for the current provider from SVP.
 */
async function fetchCategories(vendor: Vendor) {
  const response = await ensureFetchDelay(
    svpApiClient.getCategories<SvpCategoryAdditional>({
      additional: true,
      vendor,
    })
  );

  return response._embedded?.categories ?? [];
}

function findCategoryById(
  categories: SvpCategoryAdditional[],
  categoryId: SvpCategory['id']
): SvpCategoryAdditional | undefined {
  return categories.find((category) => category.id === categoryId);
}

const WEBBTV_CATEGORY_ID = 3;

// store the fetched categories for the session duration
let categories: SvpCategoryAdditional[] | undefined;

/**
 * Traverses through the {@link SvpCategory} tree and returns a list of categories
 * short paths up to the root (or a given `rootCategoryId`).
 *
 * @example `['webbtv','sport','Skidor']`
 */
async function getCategoryChain(
  categoryId: SvpCategory['id'],
  rootCategoryId: number = WEBBTV_CATEGORY_ID
) {
  if (!categories) {
    categories = await fetchCategories(config.provider);
  }

  let category = findCategoryById(categories!, categoryId);

  if (category === undefined) {
    return [];
  }

  const chain = [category];

  while (category.parentId && category.id !== rootCategoryId) {
    category = findCategoryById(categories!, category.parentId);

    if (!category) {
      break;
    }

    chain.push(category);
  }

  return chain.reverse().map(getCategoryShortPath);
}

export { getCategoryChain };
